import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { webchatAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3, MessageCircle, UserPlus, Clock, TrendingUp, Target,
  DollarSign, Globe, Loader2, AlertTriangle, Users, PhoneOff, Zap
} from 'lucide-react';
import { WebsiteChatNav } from '../components/WebsiteChatNav';

function fmtTime(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function fmtMoney(val) {
  if (!val) return '$0';
  return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtPct(val) {
  if (val == null) return '0%';
  return `${val}%`;
}

export default function WebsiteChatAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await webchatAPI.getAnalytics(parseInt(period));
      setData(res.data);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <Layout>
        <WebsiteChatNav />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const core = data?.core || {};
  const business = data?.business || {};
  const ops = data?.operational || {};
  const hasData = core.total_sessions > 0;
  const hasBusinessData = (business.pipeline_opportunities || 0) + (business.estimates_created || 0) + (business.jobs_created || 0) > 0;

  return (
    <Layout>
      <WebsiteChatNav />
      <div className="p-4 sm:p-6 space-y-6" data-testid="webchat-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              Website Chat Analytics
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Performance metrics for your website chat widget
            </p>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!hasData ? (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="no-data-state">
            <MessageCircle className="w-12 h-12 text-muted-foreground/20 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No chat data yet</p>
            <p className="text-sm text-muted-foreground/60 mt-1 max-w-sm">
              Once visitors start using your chat widget, analytics will appear here.
            </p>
          </div>
        ) : (
          <>
            {/* ─── Core KPIs ─── */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Core Metrics</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <KPICard icon={MessageCircle} label="Total Sessions" value={core.total_sessions} />
                <KPICard icon={UserPlus} label="Intakes Submitted" value={core.total_intakes} />
                <KPICard icon={Target} label="Conversion Rate" value={fmtPct(core.chat_to_lead_conversion_rate)} accent={core.chat_to_lead_conversion_rate >= 50 ? 'green' : core.chat_to_lead_conversion_rate >= 25 ? 'amber' : 'red'} />
                <KPICard icon={Clock} label="Avg Response" value={fmtTime(core.avg_first_response_seconds)} accent={core.avg_first_response_seconds != null && core.avg_first_response_seconds < 120 ? 'green' : core.avg_first_response_seconds != null && core.avg_first_response_seconds < 600 ? 'amber' : 'default'} />
                <KPICard icon={Clock} label="Median Response" value={fmtTime(core.median_first_response_seconds)} />
              </div>
            </div>

            {/* Live vs Fallback breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card data-testid="live-chat-card">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Live Conversations</p>
                      <p className="text-2xl font-bold mt-1">{core.live_chat_conversations}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Staff responded in real time</p>
                </CardContent>
              </Card>
              <Card data-testid="fallback-card">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Fallback Captures</p>
                      <p className="text-2xl font-bold mt-1">{core.fallback_captures}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Intake submitted without live staff</p>
                </CardContent>
              </Card>
              <Card data-testid="unanswered-card">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Unanswered</p>
                      <p className="text-2xl font-bold mt-1 text-amber-600">{core.unanswered_chats}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Waiting for staff reply</p>
                </CardContent>
              </Card>
            </div>

            {/* ─── Business Value ─── */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1">Business Value</h2>
              <p className="text-xs text-muted-foreground mb-3">Revenue attributed via webchat → lead → pipeline → job → invoice chain</p>
              {hasBusinessData ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  <KPICard icon={TrendingUp} label="Pipeline Leads" value={business.pipeline_opportunities} />
                  <KPICard icon={TrendingUp} label="Estimates" value={business.estimates_created} />
                  <KPICard icon={TrendingUp} label="Jobs Created" value={business.jobs_created} />
                  <KPICard icon={DollarSign} label="Total Revenue" value={fmtMoney(business.total_revenue)} />
                  <KPICard icon={DollarSign} label="Won Revenue" value={fmtMoney(business.won_revenue)} accent={business.won_revenue > 0 ? 'green' : 'default'} />
                  <KPICard icon={Target} label="Lead → Job" value={fmtPct(business.lead_to_job_conversion_rate)} />
                </div>
              ) : (
                <Card data-testid="no-business-data">
                  <CardContent className="py-8 text-center">
                    <DollarSign className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No leads created from website chat yet</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Create leads from the Inbox to start tracking pipeline attribution.</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* ─── Operational ─── */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Operational</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <KPICard icon={Users} label="Staff Response Rate" value={fmtPct(ops.staff_response_rate)} accent={ops.staff_response_rate >= 80 ? 'green' : ops.staff_response_rate >= 50 ? 'amber' : 'red'} />
                <KPICard icon={PhoneOff} label="Missed Chat Rate" value={fmtPct(ops.missed_chat_rate)} accent={ops.missed_chat_rate <= 10 ? 'green' : ops.missed_chat_rate <= 30 ? 'amber' : 'red'} />
                <KPICard icon={UserPlus} label="Offline Captures" value={ops.offline_captures} />
                <KPICard icon={Globe} label="Source Domains" value={ops.top_source_domains?.length || 0} />
              </div>
            </div>

            {/* ─── Detailed tables ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Domains */}
              <Card data-testid="top-domains-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-600" />
                    Top Source Domains
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ops.top_source_domains?.length > 0 ? (
                    <div className="space-y-2">
                      {ops.top_source_domains.map((d, i) => {
                        const maxCount = ops.top_source_domains[0]?.count || 1;
                        const pct = Math.round((d.count / maxCount) * 100);
                        return (
                          <div key={d.domain} className="flex items-center gap-3" data-testid={`domain-stat-${d.domain}`}>
                            <span className="text-sm text-muted-foreground w-5">{i + 1}.</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-mono truncate">{d.domain}</span>
                                <span className="text-sm font-semibold ml-2">{d.count}</span>
                              </div>
                              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4 text-center">No domain data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Peak Hours */}
              <Card data-testid="peak-hours-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Peak Chat Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ops.peak_hours?.length > 0 ? (
                    <div className="flex items-end gap-1 h-32">
                      {Array.from({ length: 24 }, (_, hour) => {
                        const entry = ops.peak_hours.find(h => h.hour === hour);
                        const count = entry?.count || 0;
                        const maxCount = Math.max(...ops.peak_hours.map(h => h.count), 1);
                        const height = count > 0 ? Math.max(8, Math.round((count / maxCount) * 100)) : 2;
                        return (
                          <div key={hour} className="flex-1 flex flex-col items-center gap-1" title={`${hour}:00 — ${count} chats`}>
                            <div
                              className={`w-full rounded-t transition-all ${count > 0 ? 'bg-blue-500' : 'bg-muted'}`}
                              style={{ height: `${height}%` }}
                            />
                            {hour % 4 === 0 && (
                              <span className="text-[9px] text-muted-foreground">{hour}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4 text-center">No hourly data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

// ─── KPI Card ───
function KPICard({ icon: Icon, label, value, accent = 'default' }) {
  const accentColors = {
    default: 'text-foreground',
    green: 'text-emerald-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
  };

  return (
    <Card data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground truncate">{label}</span>
        </div>
        <p className={`text-xl font-bold ${accentColors[accent]}`}>{value ?? '—'}</p>
      </CardContent>
    </Card>
  );
}
